<template>
  <div class="lesson-player-wrapper">
    <!-- Premium Header -->
    <div class="player-header px-4 py-2 flex items-center justify-between border-b">
      <div class="flex items-center gap-3">
        <v-btn icon color="#21427d" class="back-btn mr-1" @click="$emit('back')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="flex items-center gap-2 flex-wrap">
          <h1 class="text-base font-bold text-gray-800 m-0 truncate max-w-[280px] sm:max-w-md">
            {{ course.title }}
          </h1>
          <v-chip x-small color="#e8eef8" text-color="#21427d" class="font-semibold">{{
            course.category
          }}</v-chip>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <span class="text-xs text-gray-500 font-semibold">{{ progressPercent }}% Complete</span>
        <v-progress-circular
          :value="progressPercent"
          :size="24"
          :width="3"
          color="#21427d"
          background-color="#e2e8f0"
        />
      </div>
    </div>

    <!-- Main Player Area -->
    <div class="player-grid flex">
      <!-- Left Content: Video + Details -->
      <div class="player-main flex-1 flex flex-col">
        <!-- Video Wrapper -->
        <div class="video-container pa-6 bg-slate-950 flex justify-center items-center relative">
          <div
            class="video-card w-full max-w-[860px] overflow-hidden rounded-xl shadow-2xl relative border border-slate-800"
          >
            <!-- Video READY -->
            <div
              v-show="currentLesson && videoStatus === 'READY'"
              class="w-full relative aspect-video"
            >
              <video
                ref="videoPlayer"
                controls
                class="w-full h-full bg-black"
                @ended="onVideoEnded"
              >
                Your browser does not support the video tag.
              </video>

              <!-- Quality settings -->
              <div v-if="qualities && qualities.length > 0" class="absolute top-4 right-4 z-10">
                <v-menu offset-y transition="slide-y-transition">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      color="rgba(15, 23, 42, 0.8)"
                      dark
                      v-bind="attrs"
                      v-on="on"
                      class="quality-btn backdrop-blur-md rounded-lg font-semibold border border-gray-700/50"
                    >
                      <v-icon left small color="blue-lighten-2">mdi-cog-outline</v-icon>
                      {{ currentQualityLabel }}
                    </v-btn>
                  </template>
                  <v-list class="py-1 rounded-lg border border-slate-100" dense>
                    <v-list-item
                      v-for="q in qualities"
                      :key="q.label"
                      @click="changeQuality(q)"
                      :class="{ 'bg-blue-50 text-blue-600': currentQualityLabel === q.label }"
                      class="hover:bg-slate-50 py-1"
                    >
                      <v-list-item-title class="text-xs font-semibold">{{
                        q.label
                      }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>

            <!-- Loading & states inside video wrapper -->
            <div
              v-if="currentLesson && videoStatus === 'loading'"
              class="aspect-video bg-slate-950 flex flex-col justify-center items-center text-center"
            >
              <v-progress-circular indeterminate color="primary" size="48" class="mb-3" />
              <p class="text-slate-300 text-sm font-medium">Configuring stream...</p>
            </div>

            <div
              v-else-if="currentLesson && videoStatus === 'PROCESSING'"
              class="aspect-video bg-slate-950 flex flex-col justify-center items-center text-center pa-6"
            >
              <v-progress-circular indeterminate color="orange" size="48" class="mb-4" />
              <p class="text-slate-200 text-sm font-semibold">Video is processing on our servers</p>
              <p class="text-slate-400 text-xs mt-1">We will check readiness again in 30 seconds</p>
            </div>

            <div
              v-else-if="currentLesson && videoStatus === 'error'"
              class="aspect-video bg-slate-950 flex flex-col justify-center items-center text-center pa-6"
            >
              <v-icon size="48" color="red darken-1" class="mb-3">mdi-alert-circle-outline</v-icon>
              <p class="text-slate-200 text-sm font-semibold">Unable to fetch stream url</p>
              <v-btn
                small
                dark
                color="primary"
                class="mt-4 normal-case rounded-lg"
                depressed
                @click="_loadStream"
              >
                Retry Stream
              </v-btn>
            </div>

            <div
              v-else-if="!currentLesson"
              class="aspect-video bg-slate-950 flex flex-col justify-center items-center text-center pa-6"
            >
              <v-icon size="64" color="slate-600" class="mb-3">mdi-play-circle-outline</v-icon>
              <p class="text-slate-300 text-lg font-bold">Select a lesson to begin learning</p>
            </div>
          </div>
        </div>

        <!-- Lesson Meta Details -->
        <div class="lesson-meta-box pa-6 border-b flex-1" v-if="currentLesson">
          <div class="flex items-start justify-between flex-wrap gap-4 mb-4">
            <div>
              <span
                class="text-xs font-bold text-[#21427d] bg-[#e8eef8] px-2.5 py-1 rounded-full uppercase tracking-wider mb-2 inline-block"
              >
                Lesson {{ currentLesson.order }} of {{ course.lessons.length }}
              </span>
              <h2 class="text-2xl font-bold text-gray-800 m-0 leading-snug">
                {{ currentLesson.title }}
              </h2>
              <div class="flex items-center gap-3 mt-2 text-xs text-gray-500 font-medium">
                <span class="flex items-center"
                  ><v-icon left x-small>mdi-clock-outline</v-icon>{{ currentLesson.duration }}</span
                >
                <span class="flex items-center"
                  ><v-icon left x-small>mdi-school-outline</v-icon>{{ course.category }}</span
                >
              </div>
            </div>
          </div>

          <v-divider class="my-5" />

          <h4 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
            About this Lesson
          </h4>
          <p class="text-sm text-gray-600 leading-relaxed max-w-4xl mb-6">
            {{ currentLesson.description }}
          </p>

          <!-- Footer Navigation Buttons -->
          <div class="flex items-center justify-between border-t pt-5">
            <v-btn
              outlined
              class="normal-case rounded-lg font-bold"
              :disabled="currentLessonIndex <= 0"
              @click="goToLesson(currentLessonIndex - 1)"
              color="#21427d"
            >
              <v-icon left>mdi-chevron-left</v-icon>Previous
            </v-btn>
            <v-btn
              outlined
              class="normal-case rounded-lg font-bold"
              :disabled="currentLessonIndex >= course.lessons.length - 1"
              @click="goToLesson(currentLessonIndex + 1)"
              color="#21427d"
            >
              Next<v-icon right>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Right Sidebar - Lesson List -->
      <div class="playlist-sidebar flex flex-col">
        <div class="sidebar-info-panel border-b px-5 py-4">
          <h3 class="text-sm font-bold text-gray-800 mb-1 flex items-center justify-between">
            <span>Course Outline</span>
            <span class="text-xs font-bold text-blue-600"
              >{{ completedCount }}/{{ course.lessons.length }} Done</span
            >
          </h3>
          <div class="flex items-center gap-2 mt-2">
            <v-progress-linear
              :value="progressPercent"
              color="#21427d"
              height="6"
              rounded
              background-color="#e2e8f0"
              class="flex-1"
            />
          </div>
        </div>

        <div class="playlist-scroll overflow-y-auto">
          <div
            v-for="(lesson, index) in course.lessons"
            :key="lesson._id"
            class="playlist-item px-5 py-4 cursor-pointer border-b"
            :class="{
              'playlist-item-active': currentLesson && currentLesson._id === lesson._id,
              'playlist-item-done': isLessonDone(lesson._id)
            }"
            @click="goToLesson(index)"
          >
            <div class="flex items-start gap-3">
              <!-- Indicators -->
              <div class="flex-shrink-0 mt-0.5">
                <v-icon v-if="isLessonDone(lesson._id)" color="#4caf50">mdi-check-circle</v-icon>
                <v-icon
                  v-else-if="currentLesson && currentLesson._id === lesson._id"
                  color="#21427d"
                  >mdi-play-circle</v-icon
                >
                <v-icon v-else color="#94a3b8">mdi-play-circle-outline</v-icon>
              </div>

              <div class="flex-1 min-w-0">
                <h4
                  class="text-sm font-bold text-slate-700 m-0 truncate leading-snug"
                  :style="{
                    color: currentLesson && currentLesson._id === lesson._id ? '#21427d' : ''
                  }"
                >
                  {{ index + 1 }}. {{ lesson.title }}
                </h4>
                <div class="flex items-center gap-3 mt-1 text-xxs text-slate-400 font-semibold">
                  <span class="flex items-center"
                    ><v-icon left size="10">mdi-clock-outline</v-icon>{{ lesson.duration }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Hls from 'hls.js'
import {
  isLessonCompleted,
  markLessonComplete,
  markLessonIncomplete,
  getCourseProgress,
  getCompletedLessonsCount,
  state,
  initLearningData
} from './learningData'

export default {
  name: 'LessonPlayer',
  props: {
    course: { type: Object, required: true },
    initialLessonId: { type: String, default: null },
    currentUserId: { type: String, default: 'u1' }
  },
  data() {
    return {
      currentLessonIndex: 0,
      videoStatus: null, // null | 'loading' | 'PROCESSING' | 'READY' | 'error'
      qualities: [],
      currentQualityLabel: '1080p'
    }
  },
  computed: {
    currentLesson() {
      return this.course.lessons[this.currentLessonIndex] || null
    },
    isCurrentLessonCompleted() {
      if (!this.currentLesson) return false
      void state.progress.length
      return isLessonCompleted(this.course._id, this.currentLesson._id, this.currentUserId)
    },
    completedCount() {
      void state.progress.length
      return getCompletedLessonsCount(this.course._id, this.currentUserId)
    },
    progressPercent() {
      void state.progress.length
      return getCourseProgress(this.course._id, this.currentUserId)
    }
  },
  created() {
    if (this.initialLessonId) {
      const idx = this.course.lessons.findIndex((l) => l._id === this.initialLessonId)
      if (idx > -1) this.currentLessonIndex = idx
    }
  },
  mounted() {
    if (this.currentLesson) this._loadStream()
  },
  watch: {
    currentLesson(lesson) {
      this._clearPoll()
      this.videoStatus = null
      this.$nextTick(() => {
        if (lesson) this._loadStream()
      })
    }
  },
  beforeDestroy() {
    this._clearPoll()
    this._destroyHls()
  },
  methods: {
    isLessonDone(lessonId) {
      void state.progress.length
      return isLessonCompleted(this.course._id, lessonId, this.currentUserId)
    },
    goToLesson(index) {
      if (index >= 0 && index < this.course.lessons.length) {
        this.currentLessonIndex = index
      }
    },
    async onVideoEnded() {
      if (this.currentLesson && !this.isCurrentLessonCompleted) {
        await markLessonComplete(
          this.$axios,
          this.course._id,
          this.currentLesson._id,
          this.currentUserId
        )
        await initLearningData(this.$axios)
      }
      if (this.currentLessonIndex < this.course.lessons.length - 1) {
        this.goToLesson(this.currentLessonIndex + 1)
      }
    },

    async _loadStream() {
      const lesson = this.currentLesson
      console.log('[LessonPlayer] _loadStream started for lesson:', lesson)
      if (!lesson) {
        console.log('[LessonPlayer] No current lesson found.')
        return
      }

      this._clearPoll()
      this._destroyHls()

      if (!lesson.contentId) {
        console.log(
          '[LessonPlayer] Lesson has no contentId. Falling back to sample video URL:',
          lesson.videoUrl
        )
        this.videoStatus = 'READY'
        await this.$nextTick()
        this._attachSrc(lesson.videoUrl)
        return
      }

      console.log(
        '[LessonPlayer] Lesson has contentId:',
        lesson.contentId,
        'Fetching stream info...'
      )
      this.videoStatus = 'loading'

      try {
        const courseId = this.course.rawCourse?._id || this.course._id
        const targetUrl = `/learning_center/video/stream/${courseId}/${lesson.contentId}`
        console.log('[LessonPlayer] GET Requesting URL:', targetUrl)
        const res = await this.$axios.get(targetUrl)
        const data = res.data
        console.log('[LessonPlayer] API response data:', data)

        if (data.hlsStatus !== 'READY') {
          console.log(
            '[LessonPlayer] Video not ready yet. Status:',
            data.hlsStatus,
            'Scheduling polling...'
          )
          this.videoStatus = 'PROCESSING'
          this._pollTimer = setTimeout(() => this._loadStream(), 30000)
          return
        }

        console.log('[LessonPlayer] Stream READY. signed URL:', data.streamUrl)
        this.qualities = data.qualities || []
        this.currentQualityLabel = this.qualities.length > 0 ? this.qualities[0].label : '1080p'
        this.videoStatus = 'READY'
        await this.$nextTick()
        this._attachHls(data.streamUrl)
      } catch (err) {
        console.error('[LessonPlayer] Stream load error:', err)
        this.videoStatus = 'error'
      }
    },

    changeQuality(quality) {
      if (this.currentQualityLabel === quality.label) return
      this.currentQualityLabel = quality.label
      const video = this.$refs.videoPlayer
      if (!video) return

      const currentTime = video.currentTime
      const isPaused = video.paused

      this._destroyHls()
      this._attachHls(quality.url)

      this.$nextTick(() => {
        video.currentTime = currentTime
        if (!isPaused) {
          video.play().catch((e) => console.log('Autoplay play interrupted:', e))
        }
      })
    },

    _attachHls(url) {
      const video = this.$refs.videoPlayer
      if (!video) return
      if (Hls.isSupported()) {
        this._hls = new Hls()
        this._hls.loadSource(url)
        this._hls.attachMedia(video)
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url
      }
    },

    _attachSrc(url) {
      this.qualities = []
      this.currentQualityLabel = 'Default'
      const video = this.$refs.videoPlayer
      if (video) video.src = url
    },

    _clearPoll() {
      if (this._pollTimer) {
        clearTimeout(this._pollTimer)
        this._pollTimer = null
      }
    },

    _destroyHls() {
      if (this._hls) {
        this._hls.destroy()
        this._hls = null
      }
    }
  }
}
</script>

<style scoped>
.lesson-player-wrapper {
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  /* height: calc(100vh - 120px); */
}

.player-header {
  background: white;
  min-height: 48px;
}

.back-btn {
  transition: transform 0.2s ease;
}
.back-btn:hover {
  transform: translateX(-3px);
}

.player-grid {
  flex: 1;
  overflow: hidden;
}

.player-main {
  flex: 1 1 auto;
  overflow-y: auto;
}

.video-container {
  background: #0f172a;
}

.video-card {
  transition: all 0.3s ease;
}

.lesson-meta-box {
  background: white;
}

.playlist-sidebar {
  width: 500px;
  flex: 0 0 500px;
  background: white;
  border-left: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.playlist-scroll {
  flex: 1;
}

.playlist-item {
  transition: all 0.2s ease;
  background: white;
}

.playlist-item:hover {
  background: #f8fafc;
}

.playlist-item-active {
  background: #e8eef8 !important;
  border-left: 4px solid #21427d;
}

.playlist-item-done {
  background: #fdfdfd;
}
</style>
